import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Layout, LayoutContent, LayoutFooter, HStack } from "@astryxdesign/core/Layout";
import { VStack } from "@astryxdesign/core/VStack";
import { FormLayout } from "@astryxdesign/core/FormLayout";
import { TextInput } from "@astryxdesign/core/TextInput";
import { Selector } from "@astryxdesign/core/Selector";
import { Switch } from "@astryxdesign/core/Switch";
import { CheckboxInput } from "@astryxdesign/core/CheckboxInput";
import { Button } from "@astryxdesign/core/Button";
import { Banner } from "@astryxdesign/core/Banner";
import { Text } from "@astryxdesign/core/Text";
import { Token } from "@astryxdesign/core/Token";
import { StatusDot } from "@astryxdesign/core/StatusDot";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { HardDrive, Search } from "lucide-react";
import { api } from "../../lib/api";
import type { Device, ImportChannelInput, ScanResult, ScannedChannel, StreamQuality } from "../../lib/api";
import { useIsNarrowViewport } from "../../lib/responsive";

// The recorder's control port when the operator leaves it blank. Mirrors
// model.Device.EffectiveHTTPPort — kept in sync by hand, since the two run in
// different languages.
const DEFAULT_HTTP_PORT = "80";
const DEFAULT_HTTP_PORT_TLS = "443";

const SCAN_PLACEHOLDER_ROWS = 4;

interface ConnectionValues {
  name: string;
  projectId: string;
  location: string;
  host: string;
  httpPort: string;
  useTls: boolean;
  username: string;
  password: string;
}

type FieldErrors = Partial<Record<keyof ConnectionValues, string>>;

const EMPTY_CONNECTION: ConnectionValues = {
  name: "",
  projectId: "",
  location: "",
  host: "",
  httpPort: DEFAULT_HTTP_PORT,
  useTls: false,
  username: "",
  password: "",
};

/** What the operator chose for one channel, before it is sent. */
interface ChannelChoice {
  selected: boolean;
  streamQuality: StreamQuality;
  /** null means "accept what the scan found". */
  ptzEnabled: boolean | null;
  enabled: boolean;
}

interface NvrImportDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Set when re-scanning a recorder that already exists, so the connection
   *  step is skipped entirely — the operator is adding channels, not a box. */
  existingDevice?: Device | null;
  onImported: (created: number, skipped: number) => void;
}

/**
 * The two-step add-a-recorder flow: describe the box, then pick its cameras.
 *
 * The steps exist because they answer different questions and fail
 * differently. Step one is "can this server reach the recorder with these
 * credentials", which an operator must find out while still looking at the
 * password field. Step two is "which of these cameras do you want", which is
 * only answerable once the first succeeded. Collapsing them into one form
 * would mean a typo in the password surfaces as an empty list.
 */
export function NvrImportDialog({ isOpen, onOpenChange, existingDevice, onImported }: NvrImportDialogProps) {
  const isNarrow = useIsNarrowViewport();
  const pairDirection = isNarrow ? "vertical" : "horizontal";

  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: api.projects.list });

  const [values, setValues] = useState<ConnectionValues>(EMPTY_CONNECTION);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [requestError, setRequestError] = useState<string | null>(null);
  const [scan, setScan] = useState<ScanResult | null>(null);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [choices, setChoices] = useState<Record<number, ChannelChoice>>({});

  // Re-opening must not show the last recorder's channels. Reset on open
  // rather than on close: a dialog animating out with its content already
  // cleared flickers.
  useEffect(() => {
    if (!isOpen) return;

    setFieldErrors({});
    setRequestError(null);
    setChoices({});

    if (existingDevice) {
      // The box is known; only its channel list is in question.
      setValues({ ...EMPTY_CONNECTION, name: existingDevice.name });
      setDeviceId(existingDevice.id);
      setScan(null);
    } else {
      setValues(EMPTY_CONNECTION);
      setDeviceId(null);
      setScan(null);
    }
  }, [isOpen, existingDevice]);

  const set =
    <K extends keyof ConnectionValues>(key: K) =>
    (next: ConnectionValues[K]) => {
      setValues((prev) => ({ ...prev, [key]: next }));
      setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    };

  // Scanning an already-saved recorder goes straight to its channel list.
  const scanExisting = useMutation({
    mutationFn: (id: string) => api.devices.scan(id),
    onSuccess: (result) => {
      setScan(result);
      setChoices(defaultChoices(result.channels));
      setRequestError(null);
    },
    onError: (err: unknown) => setRequestError(messageOf(err)),
  });

  useEffect(() => {
    if (isOpen && existingDevice && !scan && !scanExisting.isPending) {
      scanExisting.mutate(existingDevice.id);
    }
    // scanExisting is a stable mutation object; including it would re-fire on
    // every state change it makes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existingDevice, scan]);

  // Probing an unsaved recorder: reach it first, and only create the row once
  // it answered. A recorder saved before anyone confirmed it responds is a
  // row whose every later action fails.
  const probe = useMutation({
    mutationFn: () =>
      api.devices.probe({
        vendor: "hikvision",
        host: values.host.trim(),
        port: 0,
        http_port: parsePort(values.httpPort),
        use_tls: values.useTls,
        username: values.username,
        password: values.password,
      }),
    onSuccess: (result) => {
      setScan(result);
      setChoices(defaultChoices(result.channels));
      setRequestError(null);
    },
    onError: (err: unknown) => setRequestError(messageOf(err)),
  });

  const importMutation = useMutation({
    mutationFn: async (channels: ImportChannelInput[]) => {
      let id = deviceId;
      if (!id) {
        // Created only now, once the recorder has proven reachable.
        const created = await api.devices.create({
          project_id: values.projectId,
          name: values.name.trim(),
          location: values.location.trim(),
          vendor: "hikvision",
          host: values.host.trim(),
          port: 0,
          http_port: parsePort(values.httpPort),
          use_tls: values.useTls,
          username: values.username,
          password: values.password,
        });
        id = created.id;
        setDeviceId(id);
      }
      return api.devices.import(id, channels);
    },
    onSuccess: (result) => {
      onImported(result.created.length, result.skipped_channels.length);
      onOpenChange(false);
    },
    onError: (err: unknown) => setRequestError(messageOf(err)),
  });

  const importable = useMemo(
    () => (scan?.channels ?? []).filter((c) => !c.imported_camera_id),
    [scan],
  );
  const selectedCount = importable.filter((c) => choices[c.channel_id]?.selected).length;

  function validateConnection(): boolean {
    const next: FieldErrors = {};
    if (!values.name.trim()) next.name = "Give the recorder a name";
    if (!values.projectId) next.projectId = "Choose a site";
    if (!values.host.trim()) next.host = "Host is required";
    else if (values.host.includes("://") || values.host.includes("/")) {
      next.host = "Enter just the hostname or IP, without http:// or a path";
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  }

  const statusFor = (key: keyof ConnectionValues) =>
    fieldErrors[key] ? ({ type: "error", message: fieldErrors[key] } as const) : undefined;

  function toggleChannel(channelId: number, patch: Partial<ChannelChoice>) {
    setChoices((prev) => ({
      ...prev,
      [channelId]: { ...defaultChoice(), ...prev[channelId], ...patch },
    }));
  }

  function submitImport() {
    const channels: ImportChannelInput[] = importable
      .filter((c) => choices[c.channel_id]?.selected)
      .map((c) => {
        const choice = choices[c.channel_id] ?? defaultChoice();
        return {
          channel_id: c.channel_id,
          name: "",
          ptz_enabled: choice.ptzEnabled,
          stream_quality: choice.streamQuality,
          enabled: choice.enabled,
        };
      });
    importMutation.mutate(channels);
  }

  const isScanning = probe.isPending || scanExisting.isPending;

  // Shared by the connection form's onEnter (below) and the footer's "Scan
  // recorder" button — see its own comment on why the existingDevice branch
  // is required, not optional convenience.
  const scanConnection = () => {
    if (isScanning) return;
    if (existingDevice) {
      // A brand-new recorder's fields (name/site/host) live in the form
      // below, guarded by `!scan && !existingDevice` — for a re-scan they
      // are never rendered at all, because the box's own connection details
      // are already stored server-side and there is nothing here to
      // validate. validateConnection() would still run against this
      // component's *empty* initial `values` (only `name` is pre-filled;
      // projectId/host never are for this flow) and fail every time,
      // silently, since the fields it names errors against are off-screen.
      // That was the rescan dead-end: the automatic scan on open (below)
      // could fail once, and this button — the only way to retry — then
      // did nothing at all, forever, with the only way out being Cancel.
      scanExisting.mutate(existingDevice.id);
      return;
    }
    if (!validateConnection()) return;
    probe.mutate();
  };

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} purpose="form" width={scan ? 720 : 520}>
      <Layout
        header={
          <DialogHeader
            title={existingDevice ? `Scan ${existingDevice.name}` : "Add recorder (NVR)"}
            subtitle={
              scan
                ? `${scan.model || "Recorder"} · ${scan.channels.length} channel${scan.channels.length === 1 ? "" : "s"}`
                : "Credentials are encrypted before they're stored."
            }
            onOpenChange={onOpenChange}
          />
        }
        content={
          <LayoutContent>
            <VStack gap={3}>
              {requestError && <Banner status="error" title="Couldn't reach the recorder" description={requestError} />}

              {!scan && !existingDevice && (
                <FormLayout>
                  <TextInput
                    label="Name"
                    value={values.name}
                    onChange={set("name")}
                    status={statusFor("name")}
                    placeholder="Site recorder"
                    isRequired
                    onEnter={scanConnection}
                  />
                  <FormLayout direction={pairDirection}>
                    <Selector
                      label="Project (site)"
                      value={values.projectId}
                      onChange={set("projectId")}
                      options={(projects ?? []).map((p) => ({ value: p.id, label: p.name }))}
                      placeholder="Choose a project"
                      status={statusFor("projectId")}
                      isRequired
                    />
                    <TextInput
                      label="Location"
                      value={values.location}
                      onChange={set("location")}
                      placeholder="Control room"
                      onEnter={scanConnection}
                    />
                  </FormLayout>
                  <FormLayout direction={pairDirection}>
                    <TextInput
                      label="Host"
                      value={values.host}
                      onChange={set("host")}
                      status={statusFor("host")}
                      placeholder="10.0.0.10"
                      isRequired
                      onEnter={scanConnection}
                    />
                    <TextInput
                      label="Control port"
                      value={values.httpPort}
                      onChange={set("httpPort")}
                      // Not the RTSP port: the channel list is read over the
                      // recorder's HTTP API while video comes over RTSP.
                      description="HTTP port the channel list is read over"
                      onEnter={scanConnection}
                    />
                  </FormLayout>
                  <FormLayout direction={pairDirection}>
                    <TextInput
                      label="Username"
                      value={values.username}
                      onChange={set("username")}
                      onEnter={scanConnection}
                    />
                    <TextInput
                      label="Password"
                      type="password"
                      value={values.password}
                      onChange={set("password")}
                      onEnter={scanConnection}
                    />
                  </FormLayout>
                  <Switch
                    label="Use HTTPS for control"
                    description="Some recorders close port 80 and answer only on 443."
                    value={values.useTls}
                    changeAction={(next) => {
                      set("useTls")(next);
                      // Only move the port if it is still a default, so an
                      // operator who typed 8443 does not lose it.
                      setValues((prev) =>
                        prev.httpPort === DEFAULT_HTTP_PORT || prev.httpPort === DEFAULT_HTTP_PORT_TLS
                          ? { ...prev, httpPort: next ? DEFAULT_HTTP_PORT_TLS : DEFAULT_HTTP_PORT }
                          : prev,
                      );
                    }}
                  />
                </FormLayout>
              )}

              {isScanning && (
                <VStack gap={2}>
                  {Array.from({ length: SCAN_PLACEHOLDER_ROWS }, (_, i) => (
                    // eslint-disable-next-line react/no-array-index-key -- fixed-count placeholders, never reordered
                    <Skeleton key={i} height={52} radius={2} index={i} />
                  ))}
                </VStack>
              )}

              {scan && !isScanning && scan.channels.length === 0 && (
                <EmptyState
                  icon={<HardDrive size={32} />}
                  title="This recorder reports no cameras"
                  description="It answered, but has no channels configured. Add cameras on the recorder itself, then scan again."
                />
              )}

              {scan && !isScanning && scan.channels.length > 0 && (
                <VStack gap={2}>
                  {scan.channels.map((channel) => (
                    <ChannelRow
                      key={channel.channel_id}
                      channel={channel}
                      choice={choices[channel.channel_id] ?? defaultChoice()}
                      isNarrow={isNarrow}
                      onChange={(patch) => toggleChannel(channel.channel_id, patch)}
                    />
                  ))}
                </VStack>
              )}
            </VStack>
          </LayoutContent>
        }
        footer={
          <LayoutFooter>
            <HStack gap={2} justify="end">
              <Button
                label="Cancel"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                isDisabled={isScanning || importMutation.isPending}
              />
              {!scan ? (
                <Button label="Scan recorder" variant="primary" icon={<Search />} isLoading={isScanning} onClick={scanConnection} />
              ) : (
                <Button
                  label={selectedCount === 0 ? "Select cameras" : `Import ${selectedCount} camera${selectedCount === 1 ? "" : "s"}`}
                  variant="primary"
                  isDisabled={selectedCount === 0}
                  isLoading={importMutation.isPending}
                  onClick={submitImport}
                />
              )}
            </HStack>
          </LayoutFooter>
        }
      />
    </Dialog>
  );
}

interface ChannelRowProps {
  channel: ScannedChannel;
  choice: ChannelChoice;
  isNarrow: boolean;
  onChange: (patch: Partial<ChannelChoice>) => void;
}

/** One channel in the pick list. */
function ChannelRow({ channel, choice, isNarrow, onChange }: ChannelRowProps) {
  const alreadyImported = Boolean(channel.imported_camera_id);

  return (
    <HStack gap={2} vAlign="start">
      <CheckboxInput
        label={`Import channel ${channel.channel_id}`}
        isLabelHidden
        value={alreadyImported ? true : choice.selected}
        // An already-imported channel is shown ticked and locked rather than
        // hidden: an operator re-scanning needs to see that it is accounted
        // for, not wonder where it went.
        isDisabled={alreadyImported}
        changeAction={(next: boolean) => onChange({ selected: next === true })}
      />
      <VStack gap={0.5}>
        <HStack gap={1.5} vAlign="center" wrap="wrap">
          <StatusDot
            variant={channel.online ? "success" : "neutral"}
            label={channel.online ? "Online" : "Offline"}
          />
          <Text weight="medium">
            {channel.channel_id}. {channel.name || `Channel ${channel.channel_id}`}
          </Text>
          {alreadyImported && <Token color="default" label="Already imported" size="sm" />}
          {channel.ptz_capable && <Token color="purple" label="PTZ" size="sm" />}
        </HStack>

        <HStack gap={1.5} vAlign="center" wrap="wrap">
          <Text type="supporting" color="secondary">
            {channel.codec} · {channel.width}×{channel.height}
          </Text>
          {channel.model && (
            <Text type="supporting" color="secondary">
              {channel.model}
            </Text>
          )}
          {!isNarrow && channel.source_host && (
            <Text type="code" color="secondary">
              {channel.source_host}
            </Text>
          )}
        </HStack>

        {!alreadyImported && choice.selected && (
          <HStack gap={3} vAlign="center" wrap="wrap">
            <Switch
              label="Start streaming"
              value={choice.enabled}
              changeAction={(next) => onChange({ enabled: next })}
            />
            {channel.sub_stream_path && (
              // A choice rather than a "use sub stream" toggle, because the
              // default is now sub: a toggle whose unticked state used to mean
              // main and now means sub would be the same control saying the
              // opposite thing.
              <Switch
                label="Full resolution"
                description="Costs roughly 20x the bandwidth of the sub stream"
                value={choice.streamQuality === "main"}
                changeAction={(next) => onChange({ streamQuality: next ? "main" : "sub" })}
              />
            )}
            <Switch
              label="PTZ"
              // Discovery is conservative and reports third-party domes as
              // fixed, so this stays editable rather than being shown as a
              // settled fact.
              value={choice.ptzEnabled ?? channel.ptz_capable}
              changeAction={(next) => onChange({ ptzEnabled: next })}
            />
          </HStack>
        )}
      </VStack>
    </HStack>
  );
}

function defaultChoice(): ChannelChoice {
  // Sub by default: an imported recorder channel is a wall tile, and a wall is
  // what a mining site's uplink cannot afford at full resolution.
  return { selected: false, streamQuality: "sub", ptzEnabled: null, enabled: true };
}

/** Pre-selects every channel that is not already imported. */
function defaultChoices(channels: ScannedChannel[]): Record<number, ChannelChoice> {
  const out: Record<number, ChannelChoice> = {};
  for (const channel of channels) {
    out[channel.channel_id] = { ...defaultChoice(), selected: !channel.imported_camera_id };
  }
  return out;
}

function parsePort(value: string): number {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : "Unknown error";
}
